<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDeviceStore } from '@/stores/device'
import { useRouter, useRoute } from 'vue-router'

const props = defineProps<{ collapsed: boolean; activePopup: string | null }>()
const emit = defineEmits<{
  (e: 'toggle'): void
  (e: 'toggle-popup', popupId: string): void
  (e: 'close-popup'): void
}>()

const { t } = useI18n()
const deviceStore = useDeviceStore()
const router = useRouter()
const route = useRoute()

interface MenuItem {
  id: string
  icon: string
  iconHover: string
  label: string
  route?: string
  popup?: string
  show: boolean
}

const menuItems = computed<MenuItem[]>(() => [
  {
    id: 'eq',
    icon: '/img/nav/EQ.png',
    iconHover: '/img/nav/EQ_hover.png',
    label: 'EQ',
    route: '/eq',
    show: true,
  },
  {
    id: 'enc',
    icon: '/img/nav/ENC.png',
    iconHover: '/img/nav/ENC_hover.png',
    label: deviceStore.isHeadPhone ? 'AI ENC' : 'ENC',
    popup: 'noise',
    show: true,
  },
  {
    id: 'chiji',
    icon: '/img/nav/specialAudio7.1.png',
    iconHover: '/img/nav/specialAudio7.1_hover.png',
    label: t('main.chickenDinner', 'Positioning'),
    route: '/chiji',
    show: true,
  },
])

// 计算当前活动菜单
const activeMenuId = computed(() => {
  if (props.activePopup === 'noise') return 'enc'
  const path = route.path
  const item = menuItems.value.find(m => m.route && path.startsWith(m.route))
  return item?.id || ''
})

// 导航到指定页面
const navigateTo = async (item: MenuItem) => {
  // popup 自身支持开关：如果当前已打开，再点一次直接关闭（对齐旧版）
  if (item.popup && props.activePopup === item.popup) {
    emit('close-popup')
    return
  }

  // 互斥：打开任意一个入口前，先关闭当前 popup
  if (props.activePopup) {
    emit('close-popup')
  }

  // ENC/AI ENC 为 popup：必须回到主页上弹出（对齐旧版：不和 EQ/吃鸡页面共存）
  if (item.popup) {
    if (route.path !== '/') {
      await router.push('/')
    }
    emit('toggle-popup', item.popup)
    return
  }

  if (!item.route) return
  if (activeMenuId.value === item.id) {
    await router.push('/')
    return
  }
  await router.push(item.route)
}

const toggleCollapse = () => {
  emit('toggle')
}
</script>

<template>
  <div class="sidebar" :class="{ collapsed: props.collapsed }">
    <!-- Logo -->
    <div class="logo-div">
      <router-link to="/">
        <img src="/img/logo.png" alt="logo" />
      </router-link>
    </div>

    <!-- 导航图标 -->
    <div class="nav-icons">
      <div
        v-for="item in menuItems"
        :key="item.id"
        v-show="item.show"
        class="nav-item"
        :class="{ active: activeMenuId === item.id }"
        @click="navigateTo(item)"
      >
        <div class="nav-icon-wrapper">
          <img :src="activeMenuId === item.id ? item.iconHover : item.icon" :alt="item.label" />
        </div>
        <p class="nav-tooltip">{{ item.label }}</p>
      </div>
    </div>

    <div class="sidebar-collapse-btn" @click="toggleCollapse">
      <span>{{ props.collapsed ? '>>' : '<<' }}</span>
    </div>
  </div>
</template>

<style scoped lang="scss">
.sidebar {
  width: var(--sidebar-width);
  height: 100vh;
  background: rgba(124, 124, 124, 0.2);
  border-right: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0;
  position: fixed;
  left: 0;
  top: 0;
  z-index: 101;
  overflow: visible;
}

.logo-div {
  width: 34px;
  height: 34px;
  margin: 20px auto 13px;

  img {
    width: 34px;
    height: 34px;
    object-fit: contain;
  }
}

.nav-icons {
  display: flex;
  flex-direction: column;
  margin-top: 28px;
  width: 100%;
  align-items: center;
}

.nav-item {
  position: relative;
  width: 100%;
  height: 58px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 0;
  margin: 8px auto;
  cursor: pointer;
  transition: transform 0.1s ease, box-shadow 0.1s ease, background 0.1s ease;
  background: transparent;

  &:hover {
    background: url('/img/nav/sidebar-btn-bg.png') no-repeat center;
    background-size: 98% 98%;
    transform: scale(1.05);
    box-shadow: 0 12px 25px rgba(0, 0, 0, 0.15);

    .nav-icon-wrapper img {
      opacity: 1;
    }

    .nav-tooltip {
      display: inline-flex;
    }
  }

  &.active {
    background: url('/img/nav/sidebar-btn-bg.png') no-repeat center;
    background-size: 100% 100%;

    .nav-icon-wrapper img {
      opacity: 1;
    }
  }
}

.nav-icon-wrapper {
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 42px;
    height: 42px;
    object-fit: contain;
    opacity: 1;
    transition: opacity 0.15s ease;
  }
}

.nav-tooltip {
  display: none;
  position: absolute;
  left: 57px;
  top: 10px;
  padding: 8px;
  margin: 0;
  font-size: 12px;
  color: rgb(255, 255, 255);
  text-align: center;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  border-radius: 8px;
  white-space: nowrap;
  align-items: center;
  justify-content: center;
  z-index: 20;
}

.sidebar-collapse-btn {
  position: absolute;
  top: 50%;
  right: -20px;
  transform: translateY(-50%);
  width: 20px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.55);
  font-size: 13px;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-left: none;
  border-radius: 0 8px 8px 0;
  cursor: pointer;
  z-index: 5;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
    color: rgba(255, 255, 255, 0.75);
  }
}

.sidebar.collapsed {
  background: transparent;
  border-right: none;
}

.sidebar.collapsed .logo-div,
.sidebar.collapsed .nav-icons {
  display: none;
}

.sidebar.collapsed .sidebar-collapse-btn {
  right: auto;
  left: 0;
}
</style>
